import React, { useEffect, useState } from 'react';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { singularAPI } from '../../lib/api';
import RundownTable from '../rundown/RundownTable';
import CueTypeBadge from '../rundown/CueTypeBadge';

function SingularPanel({ showId, show }) {
  const [compositions, setCompositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeComps, setActiveComps] = useState(new Set());

  // Same trap as elsewhere: show.singular_api_key is redacted over REST and not
  // synced at all, so under local-first it is always undefined and this panel
  // would report "not configured" for a perfectly good token. The server probe
  // in the connection store is the authority.
  const singularConfigured = useConnectionStore(s => s.singularConfigured);
  const hasSingular = singularConfigured || !!show?.singular_api_key;

  useEffect(() => {
    if (!hasSingular) return;
    loadCompositions();
  }, [showId, hasSingular]);

  async function loadCompositions() {
    setLoading(true);
    setError('');
    try {
      const res = await singularAPI.compositions(showId);
      setCompositions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load compositions');
    } finally {
      setLoading(false);
    }
  }

  async function handleShow(compositionId) {
    try {
      await singularAPI.show(showId, compositionId);
      setActiveComps(prev => new Set([...prev, compositionId]));
    } catch (err) {
      console.error('Singular show failed:', err.message);
    }
  }

  async function handleHide(compositionId) {
    try {
      await singularAPI.hide(showId, compositionId);
      setActiveComps(prev => {
        const next = new Set(prev);
        next.delete(compositionId);
        return next;
      });
    } catch (err) {
      console.error('Singular hide failed:', err.message);
    }
  }

  async function handleHideAll() {
    try {
      await singularAPI.hideAll(showId);
      setActiveComps(new Set());
    } catch (err) {
      console.error('Singular hide-all failed:', err.message);
    }
  }

  if (!hasSingular) {
    return (
      <div className="bg-app-sub border border-app-border rounded-lg p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Singular.live</h3>
        <p className="text-xs text-gray-700">Singular control token not configured for this show.</p>
      </div>
    );
  }

  return (
    <div className="bg-app-sub border border-app-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Singular.live</h3>
        <div className="flex gap-2">
          <button
            onClick={loadCompositions}
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={handleHideAll}
            className="text-xs text-red-600 hover:text-red-400 transition-colors"
          >
            Hide All
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-2 px-2 py-1.5 bg-red-900/20 border border-red-800 rounded text-red-400 text-xs">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-gray-600">Loading compositions...</div>
      ) : compositions.length === 0 ? (
        <div className="text-xs text-gray-700">No compositions found</div>
      ) : (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {compositions.map(comp => {
            const compId = comp.subCompositionId || comp.id;
            const compName = comp.mainComposition ? 'Overlay Customisation' : (comp.subCompositionName || comp.name || compId);
            const isActive = activeComps.has(compId);
            return (
              <div
                key={compId}
                className={`flex items-center justify-between px-2 py-1.5 rounded border ${
                  isActive
                    ? 'bg-purple-900/20 border-purple-800 text-purple-300'
                    : 'bg-app-header border-app-surface text-gray-400'
                }`}
              >
                <span className="text-xs truncate flex-1">{compName}</span>
                <div className="flex gap-1.5 ml-2">
                  <button
                    onClick={() => handleShow(compId)}
                    disabled={isActive}
                    className="px-2 py-0.5 text-xs bg-purple-900/40 text-purple-400 rounded hover:bg-purple-900/60 transition-colors disabled:opacity-40"
                  >
                    Show
                  </button>
                  <button
                    onClick={() => handleHide(compId)}
                    disabled={!isActive}
                    className="px-2 py-0.5 text-xs bg-app-surface text-gray-500 rounded hover:bg-app-border transition-colors disabled:opacity-40"
                  >
                    Hide
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GraphicCuesPanel() {
  const graphicCues = useShowStore(s =>
    s.cues.filter(c => c.cue_type === 'GRAPHIC' && c.status !== 'complete')
  );

  return (
    <div className="bg-app-sub border border-app-border rounded-lg p-4">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        Upcoming Graphics ({graphicCues.length})
      </h3>
      {graphicCues.length === 0 ? (
        <div className="text-xs text-gray-700">No upcoming graphic cues</div>
      ) : (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {graphicCues.map(cue => (
            <div
              key={cue.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded border ${
                cue.status === 'on_air'
                  ? 'bg-red-900/20 border-red-800'
                  : 'bg-app-header border-app-surface'
              }`}
            >
              <CueTypeBadge type={cue.cue_type} compact />
              <span className={`text-xs flex-1 truncate ${
                cue.status === 'on_air' ? 'text-app-fg font-medium' : 'text-gray-400'
              }`}>
                {cue.label}
              </span>
              {cue.status === 'on_air' && (
                <span className="text-xs text-red-400 font-bold">LIVE</span>
              )}
              {(cue.cue_actions || []).some(a => a.type === 'singular') && (
                <span className="text-xs text-purple-600" title="Has Singular action">S</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GraphicsView({ showId, show }) {
  return (
    <div className="flex h-full">
      {/* Left: Rundown */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <RundownTable showId={showId} readOnly={true} />
      </div>

      {/* Right: Graphics Panels */}
      <div className="w-80 flex-shrink-0 border-l border-app-border overflow-y-auto bg-app-bg p-3 space-y-3">
        <GraphicCuesPanel />
        <SingularPanel showId={showId} show={show} />
      </div>
    </div>
  );
}
